import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import { schema } from '@acme/core'
import dayjs from 'dayjs'
import { FormDatePicker, FormField } from 'global/components/Form'
import { SelectEmpresa, SelectFuncionario } from 'global/components/Selects'
import { ContentGuide } from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import { Trash2Icon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useMutation } from 'react-query'
import { Link, useParams } from 'react-router-dom'
import { Funcionario } from 'service/api'
import { escalas } from 'service/api/endpoints/escalas'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'

const escalaFuncionarioSchema = schema.EscalasTrabalhoFuncionarioSchema.partial(
    {
        dtFinal: true,
    },
)
    .pick({
        dtInicial: true,
        dtFinal: true,
    })
    .extend({
        funcionarioId: z.number().int(),
    })

type EscalaFuncionario = z.infer<typeof escalaFuncionarioSchema>

function FormularioAssocioarFuncionarioEscala() {
    const { id: escalaId } = useParams()
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)

    const methods = useFormContext<EscalaFuncionario>()

    const { data, refetch, isLoading } = escalas.useFetchByIdFrom<
        Funcionario[]
    >(escalaId!, 'funcionarios', {
        enabled: !!escalaId && !!empresaId,
        queryParams: {
            empresaId,
        },
    })

    const { mutateAsync } = useMutation(
        ['escalas', escalaId, 'funcionarios'],
        (data: EscalaFuncionario) => {
            return escalas.postFrom<EscalaFuncionario>(
                escalaId!,
                'funcionarios',
                data,
            )
        },
    )

    const handleSubmit = async (data: EscalaFuncionario) => {
        const validation = await escalaFuncionarioSchema.safeParseAsync({
            ...data,
            escalaTrabalhoId: Number(escalaId),
            funcionarioId,
        })

        if (!validation.success) {
            for (const error of validation.error.errors) {
                methods.setError(error.path[0] as any, {
                    type: 'value',
                    message: error.message,
                })
            }

            return toast.error('Erro de validação, verifique os campos')
        }

        try {
            await mutateAsync(validation.data)

            toast.success('Funcionário associado com sucesso')

            refetch()
        } catch (error: any) {
            if (error.statusCode === 400) {
                toast.error(error.message)
            } else {
                toast.error('Erro ao associar funcionário')
            }
        }
    }

    const update = async (
        escalaFuncionarioId: number,
        field: string,
        value: string | null,
    ) => {
        if (!['dtInicial', 'dtFinal'].includes(field)) return
        const template = field === 'dtInicial' ? 'Data inicial' : 'Data final'
        try {
            const parse =
                field === 'dtFinal' && !value
                    ? null
                    : z.string().date().nullish().parse(value)
            await escalas.patch('funcionarios', escalaFuncionarioId, {
                [field]: parse,
            })

            await refetch()

            toast.success(`${template} alterada com sucesso`)
        } catch (error: any) {
            if (error instanceof z.ZodError) {
                toast.error(`Erro de validação, ${template} inválida`)
            } else {
                toast.error(error.message)
            }
        }
    }

    return (
        <Grid container className="items-start">
            <Grid item xs={12}>
                <SelectEmpresa variant="form" />
            </Grid>
            {hasPermission(['update', 'Escalas'], () => (
                <form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    className="col-span-12 grid w-full h-fit grid-cols-24 gap-2"
                >
                    <Grid item xs={24}>
                        <HorizontalDivider>
                            <span className="text-base flex items-center">
                                Associar funcionário
                                <IconeAjuda>
                                    <li>
                                        Selecione um funcionário para associar a
                                        escala de trabalho, informe a data
                                        inicial e se deseja informar uma data
                                        final indeterminada.
                                    </li>
                                    <br />
                                    <li>
                                        Caso a data final seja informada, o
                                        funcionário será desassociado da escala
                                        na data informada.
                                    </li>
                                    <br />
                                    <li>
                                        Caso a data final seja indeterminada, o
                                        funcionário permanecerá associado a
                                        escala até que seja desassociado
                                        manualmente.
                                    </li>
                                </IconeAjuda>
                            </span>
                        </HorizontalDivider>
                    </Grid>
                    <Grid item xs={20}>
                        <SelectFuncionario variant="form" className="mb-2" />
                    </Grid>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            size="large"
                            type="submit"
                            className="flex-1"
                        >
                            Associar
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <FormDatePicker
                            name="dtInicial"
                            label="Data inicial"
                            type="date"
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormDatePicker
                            name="dtFinal"
                            label="Data final"
                            type="date"
                            helperText="Deixe em branco para indeterminada"
                        />
                    </Grid>
                    <FormField name="id" hidden />
                </form>
            ))}
            <Grid item xs={12}>
                <HorizontalDivider>
                    Funcionários associados
                    <IconeAjuda>
                        <ul className="pl-4">
                            <li>
                                Lista de funcionários associados a escala de
                                trabalho.
                            </li>
                            <br />
                            <li>
                                Para editar a data inicial ou final, clique na
                                célula correspondente.
                            </li>
                            <br />
                            <li>
                                Para desassociar um funcionário, clique no ícone
                                de lixeira.
                            </li>
                        </ul>
                    </IconeAjuda>
                </HorizontalDivider>
            </Grid>
            <Grid item xs={12} className="h-[360px]">
                <ContentGuide.DataGrid
                    loading={isLoading}
                    onCellEditCommit={({ field, id, value }) =>
                        update(Number(id), field, value || null)
                    }
                    columns={[
                        {
                            field: 'Funcionario',
                            headerName: 'Nome',
                            flex: 1,
                            valueGetter: ({ value }) => value.nome,
                        },
                        {
                            field: 'dtInicial',
                            headerName: 'Data Inicial',
                            width: 160,
                            disableColumnMenu: true,
                            editable: true,
                            type: 'date',
                            valueGetter: ({ value }) => {
                                return value
                                    ? dayjs.utc(value).format('YYYY-MM-DD')
                                    : null
                            },
                            valueFormatter: ({ value }) =>
                                value
                                    ? dayjs.utc(value).format('DD/MM/YYYY')
                                    : null,
                        },
                        {
                            field: 'dtFinal',
                            headerName: 'Data Final',
                            width: 160,
                            disableColumnMenu: true,
                            editable: true,
                            type: 'date',
                            valueGetter: ({ value }) => {
                                return value
                                    ? dayjs.utc(value).format('YYYY-MM-DD')
                                    : null
                            },
                            valueFormatter: ({ value }) =>
                                value
                                    ? dayjs.utc(value).format('DD/MM/YYYY')
                                    : null,
                        },
                        {
                            ...optionsColumn,
                            field: 'delete',
                            renderCell: ({ id: pivotId }) => (
                                <Link to={`${pivotId}/excluir`}>
                                    <IconButton>
                                        <Trash2Icon className="text-error" />
                                    </IconButton>
                                </Link>
                            ),
                        },
                    ]}
                    rows={data}
                    ToolbarProps={{ hideToolbar: true }}
                    hideFooter
                />
            </Grid>
        </Grid>
    )
}

export default FormularioAssocioarFuncionarioEscala
